import React from 'react'
import styles from './Gallery.scss'
import { Filmstrip } from './Filmstrip'
import { MainPhoto } from './MainPhoto'
import { Icon } from 'common/LegacyIcon'
import * as urlHelpers from 'lib/urlHelpers'
import preloadImg from 'lib/preloadImg'
import { wrapInt } from 'lib/utils'
import fetchJSON from 'lib/fetchJSON'
import { Range, List, Iterable } from 'immutable'
import { injectIntl, defineMessages } from 'react-intl'
import { withRouter } from 'react-router-dom'

export const galleryMessages = defineMessages({
  openGallery: {
    id: 'galleryMessages.openGallery',
    defaultMessage: 'Open gallery'
  }
})

class _Gallery extends React.Component {
  constructor (props) {
    super(props)

    // activePhotoId shows photo with that id, defaultActive shows photo with that index in photos array
    const { activePhotoId, defaultActive, coverId } = props
    const sieve = {}
    const photos = List(props.photos).filter(p => (!sieve[p.id]) && (sieve[p.id] = true))

    let activePhoto = 0
    if (activePhotoId) {
      [activePhoto] = photos.findEntry(photo => activePhotoId === photo.id) || [0]
    } else if (defaultActive) {
      activePhoto = defaultActive
    }

    this.state = {
      activePhoto,
      photos,
      coverId,
      mouseMode: true,
      backLocation: props.location
    }
  }

  componentDidMount () {
    this._stopListeningToHistory = this.props.history.listen(this.onLocationChange)
    const { activePhoto, photos } = this.state
    // only cache scroll if there is a vertical scrollbar.
    // when gallery is used within Overlay, this will keep from clobbering
    // the correct scroll position
    if (document.body.scrollHeight > document.body.clientHeight) {
      this._cachedScrollTop = document.body.scrollTop
    }
    document.body.classList.add('gallery-open')
    window.addEventListener('keydown', this.onKeydown)
    this.preload()
    this.pushRoute(`photos/${photos.get(activePhoto).id}`)
  }

  componentDidUpdate (prevProps, prevState) {
    const { activePhoto, photos } = this.state
    this.preload()
    if (prevProps.defaultActive !== this.props.defaultActive) {
      this.setState({ activePhoto: this.props.defaultActive })
    }
    if (prevProps.photos !== this.props.photos) {
      this.setState({ photos: List(this.props.photos) })
    }
    if (prevState.activePhoto !== activePhoto) {
      this.updateRoute(`photos/${photos.get(activePhoto).id}`)
    }
  }

  componentWillUnmount () {
    this._stopListeningToHistory()
    const { history, location } = this.props
    if (location.hash.includes('#photos')) {
      // if something calls onRequestClose without changing the URL, it's some
      // kind of UI that we should treat like a back button, popping the photo
      // viewer out of the URL state.
      //
      // This has the effect of making the back button more predictable:
      //
      // 1. User starts on dashboard
      // 2. User clicks a route, is taken to RSP
      // 3. User opens a photo, we push the photo gallery into the URL
      // 4. User hits back once, they're taken back to the RSP
      // 5. User hits back again, they're taken back to the dashboard
      //
      // the only side effect is that we can't delete history entries after
      // the user hits back, so if the user hits forward instead of back
      // after step 4, it will add the gallery fragment back into the URL,
      // but that will be a noop from a user perspective until
      // https://github.com/ridewithgps/ridewithgps/issues/6647 is addressed

      const { backLocation } = this.state
      history.replace(backLocation)
    }
    document.body.classList.remove('gallery-open')
    window.removeEventListener('keydown', this.onKeydown)
    if (this._cachedScrollTop !== undefined) {
      document.body.scrollTop = this._cachedScrollTop
    }
  }

  onLocationChange = ({ hash }) => {
    const { onRequestClose } = this.props
    // onLocationChange fires whenever we call replaceState, so guard the
    // call to onRequestClose so we don't accidentally unmount ourselves
    // when flipping through photos
    if (!hash.includes('#photos')) {
      onRequestClose && onRequestClose()
    }
  }

  onDismiss () {
    const { onDismiss, onRequestClose } = this.props
    if (onDismiss) {
      console.warn('passing an onDismiss prop to the Gallery component is deprecated, please update your code to use onRequestClose')
      onDismiss()
    }
    onRequestClose && onRequestClose()
  }

  onKeydown = (e) => {
    if (document.activeElement.contentEditable === 'true') {
      return
    } // don't process caption input
    const ESC = 27
    const ARROW_LEFT = 37
    const ARROW_RIGHT = 39
    const J = 74
    const K = 75
    switch (e.keyCode) {
      case ESC:
        this.onDismiss()
        break
      case K:
      case ARROW_LEFT:
        this.prev()
        this.setState({ mouseMode: false })
        break
      case J:
      case ARROW_RIGHT:
        this.next()
        this.setState({ mouseMode: false })
        break
    }
  }

  dismiss = () => {
    this.onDismiss()
  }

  onPhotoClick = (index) => {
    this.setState({ activePhoto: index, mouseMode: true })
  }

  next = () => {
    const { activePhoto } = this.state
    const { photos } = this.state
    this.setState({ activePhoto: wrapInt(activePhoto + 1, photos.size) })
  }

  prev = () => {
    const { activePhoto } = this.state
    const { photos } = this.state
    this.setState({ activePhoto: wrapInt(activePhoto - 1, photos.size) })
  }

  preload = () => {
    const { activePhoto, photos } = this.state

    const toPreload = Range(activePhoto - 5, activePhoto + 5)
      .map((i) => wrapInt(i, photos.size))
      .map(i => photos.get(i))
    const distinct = [...new Set(toPreload.map(p => p.id))]

    distinct
      .forEach(pid => {
        const photoFromProps = photos.find(p => p.id === pid)
        // embeds hand List({id: pid}) for photos so we need to fetch our own complete photos
        if (!('caption' in photoFromProps)) {
          fetchJSON('/photos/' + pid)
            .then(({ photo }) => {
              this.setState({ photos: photos.set(photos.indexOf(photoFromProps), photo) })
            })
        }
        preloadImg(urlHelpers.photoLarge(pid))
      })
  }

  onDelete = () => {
    const { photos, activePhoto } = this.state
    const { parentId, parentType, onPhotoDeleted } = this.props
    const photoId = photos.get(activePhoto).id
    if (photos.size === 1) {
      this.dismiss()
    } else {
      this.setState({
        photos: photos.delete(activePhoto),
        activePhoto: wrapInt(activePhoto, photos.size - 1)
      }, () => {
        const { photos, activePhoto } = this.state
        this.updateRoute(`photos/${photos.get(activePhoto).id}`)
      })
    }

    fetchJSON(`/photosets/disassociate_photo.json`, {
      parent_id: parentId,
      parent_type: parentType,
      photo_id: photoId
    })

    if (window.rwgpsG_views_photos && window.rwgpsG_views_photos.removeById) {
      window.rwgpsG_views_photos.removeById(photoId)
    }
    if (onPhotoDeleted) {
      onPhotoDeleted({ parentId, parentType, photoId })
    }
  }

  onCaptionChange = (caption) => {
    const { photos, activePhoto } = this.state
    const photo = photos.get(activePhoto)
    if (Iterable.isIterable(photo)) {
      this.setState({ photos: photos.set(activePhoto, photo.merge({ caption })) })
    } else {
      this.setState({ photos: photos.set(activePhoto, { ...photo, caption }) })
    }
  }

  pushRoute = (newRoute) => {
    const router = window.rwgpsG_views_photos && window.rwgpsG_views_photos.router
    const { history, location } = this.props
    if (router) {
      // router.navigate is legacy stuff, and *is* relative to the current url
      router.navigate(newRoute, { replace: true, trigger: false })
    } else {
      // history.push is for rwgps-ui core, and *is not* relative to the current url
      history.push({ ...location, hash: `#${newRoute}` })
    }
  }

  updateRoute = (newRoute) => {
    const router = window.rwgpsG_views_photos && window.rwgpsG_views_photos.router
    const { history, location } = this.props
    if (router) {
      router.navigate(newRoute, { replace: true, trigger: false })
    } else {
      history.replace({ ...location, hash: `#${newRoute}` })
    }
  }

  onSetCover = (coverId) => {
    this.setState({ coverId })
  }

  render () {
    const { readonly, parentType, parentId, intl } = this.props
    const { activePhoto, photos, coverId, mouseMode } = this.state

    return (
      <div className={styles.Gallery}>
        <div className={styles.exit} onClick={this.dismiss}>
          <Icon i='x' size={25} />
        </div>
        <MainPhoto
          intl={intl}
          photos={photos}
          activePhoto={activePhoto}
          next={this.next}
          prev={this.prev}
          onCaptionChange={this.onCaptionChange}
          onDelete={this.onDelete}
          readonly={readonly}
          parentType={parentType}
          parentId={parentId}
          coverId={coverId}
          onSetCover={this.onSetCover} />
        <Filmstrip intl={intl}
          photos={photos}
          activePhoto={activePhoto}
          onPhotoClick={this.onPhotoClick}
          mouseMode={mouseMode} />
      </div>
    )
  }
}

export const Gallery = withRouter(injectIntl(_Gallery))
